<template>
  <v-card
    class="float-card d-flex pa-2 flex-column"
    width="400"
    max-height="250"
  >
    <v-row no-gutters class="justify-center align-center">
      <v-progress-circular
        v-if="!allVideosCompleted"
        :value="percent"
        class="mr-2 mb-1"
        color="primary"
        width="2"
        :size="18"
      ></v-progress-circular>
      <p class="primary--text ma-0" v-if="!allVideosCompleted">
        {{
          $t('message.sendingVideos', {
            count: beingUploadedVideos,
          })
        }}
      </p>
      <p class="primary--text ma-0" v-else>
        <v-icon color="primary" class="mb-1 mr-1">
          mdi-checkbox-marked-circle
        </v-icon>
        {{ $t('message.sucessfullUploaded') }}
      </p>
      <v-spacer />
      <v-btn icon large @click="handleMinimize" v-if="opened">
        <v-icon>mdi-chevron-down</v-icon>
      </v-btn>
      <v-btn icon large @click="handleMaximize" v-else>
        <v-icon>mdi-chevron-up</v-icon>
      </v-btn>
      <v-btn icon large @click="handleConfirmCancel">
        <v-icon size="22">mdi-close</v-icon>
      </v-btn>
    </v-row>
    <v-card-text
      class="ma-0 pa-0 overflow-y-auto"
      v-scroll.self="onScroll"
      v-if="opened"
    >
      <v-hover v-slot="{ hover }" v-for="(file, i) in files" :key="file.id">
        <v-row justify="space-between" class="ma-0">
          <v-col cols="10" class="pa-1" v-if="file.error">
            <p class="text-truncate ma-0">{{ file.name }}</p>
            <p class="ma-0 text--secondary" style="font-size: 0.75rem">
              {{ $t('message.fileFailed') }}
            </p>
          </v-col>
          <v-col cols="10" class="pa-1">
            <v-row no-gutters>
              <v-col cols="12">
                <p
                  class="text-truncate ma-0"
                  @click="
                    file.meta.video_id && file.progress.uploadComplete
                      ? goToVideo(file.meta.video_id)
                      : ''
                  "
                  :style="
                    file.meta.video_id && file.progress.uploadComplete
                      ? 'cursor: pointer'
                      : ''
                  "
                >
                  {{ file.name }}
                </p>
              </v-col>
              <p
                v-if="!paused"
                class="ma-0 text--secondary"
                style="font-size: 0.75rem"
              >
                {{
                  `${calcBytes(
                    file.progress.bytesUploaded,
                    file.progress.bytesTotal
                  )} (${calcFakePercent(file.progress)}%)`
                }}
              </p>
              <p v-else class="ma-0 text--secondary" style="font-size: 0.75rem">
                {{ `${pausedBytes[i]} (${calcFakePercent(file.progress)}%)` }}
              </p>
              &nbsp;
              <v-tooltip top>
                <template v-slot:activator="{ on, attrs }">
                  <v-icon
                    :class="
                      checkIfAlreadyInPage(file.meta.folder_id) && 'noPointer'
                    "
                    v-on="on"
                    v-bind="attrs"
                    @click="goToFolder(file.meta.folder_id)"
                    :color="
                      checkIfAlreadyInPage(file.meta.folder_id)
                        ? 'grey'
                        : 'primary'
                    "
                    size="16"
                    style="margin-bottom: 1px; padding: 0px 2px"
                  >
                    mdi-folder
                  </v-icon>
                </template>
                {{
                  checkIfAlreadyInPage(file.meta.folder_id)
                    ? $t('message.pages.uploadListModal.fileAlreadyInPage')
                    : $t('message.pages.uploadListModal.goToFileFolder')
                }}
              </v-tooltip>
              <v-col
                class="text--secondary ma-0"
                style="
                  font-size: 0.75rem;
                  white-space: nowrap;
                  overflow-x: hidden;
                  white-space: nowrap !important;
                  text-overflow: ellipsis !important;
                  margin-left: -1px !important;
                "
              >
                <v-tooltip top>
                  <template v-slot:activator="{ on, attrs }">
                    <span v-on="on" v-bind="attrs" style="margin-left: -3px">
                      &nbsp;{{
                        `/${
                          file.meta.folder_id
                            ? findFolderById(file.meta.folder_id).name || {}
                            : ''
                        }`
                      }}
                    </span>
                  </template>
                  {{ getFolderPath(file.meta.folder_id, file.meta.name) }}
                </v-tooltip>
              </v-col>
            </v-row>
          </v-col>
          <v-col
            cols="2"
            class="pa-2 d-flex align-center justify-center"
            v-if="hover || file.error"
          >
            <v-btn class="ml-5" icon @click="handleRemoveFromQueue(file)">
              <v-icon size="22">mdi-close-circle</v-icon>
            </v-btn>
          </v-col>
          <v-col
            cols="2"
            class="pa-2 d-flex align-center justify-center"
            v-else
          >
            <v-progress-circular
              :value="calcFakePercent(file.progress)"
              v-if="!file.progress.uploadComplete"
              class="ml-5"
              color="primary"
              width="2"
              :size="22"
            ></v-progress-circular>
            <v-icon v-else color="primary" class="ml-6">
              mdi-checkbox-marked-circle
            </v-icon>
          </v-col>
        </v-row>
      </v-hover>
    </v-card-text>
    <v-divider v-if="opened" class="my-1" />
    <v-row no-gutters v-if="opened" class="mt-1 pr-2">
      <v-btn
        plain
        small
        class="ma-0 px-1 py-0"
        @click="handleCleanConcluded"
        v-if="atleastOneVideoCompleted"
      >
        {{ $t('message.clearDone') }}
      </v-btn>
      <v-spacer />
      <v-btn
        icon
        small
        @click="resumePause"
        v-if="!allVideosCompleted && !paused"
      >
        <v-icon color="primary"> mdi-pause </v-icon>
      </v-btn>
      <v-btn
        icon
        small
        @click="resumePause"
        :disabled="allVideosCompleted"
        v-if="paused"
      >
        <v-icon color="primary"> mdi-play </v-icon>
      </v-btn>
    </v-row>
  </v-card>
</template>

<script>
import prettyBytes from 'pretty-bytes'
import { mapMutations, mapState } from 'vuex'
export default {
  name: 'UploadList',
  props: {
    files: {
      type: Array,
      default: () => [],
    },
    percent: {
      type: Number,
      default: () => 0,
    },
    paused: {
      type: Boolean,
      required: true,
    },
    handlePause: Function,
    handleResume: Function,
    handleCancel: Function,
  },
  data: () => ({
    pausedBytes: [],
    scrollInvoked: 0,
    opened: true,
  }),
  computed: {
    ...mapState('user', {
      folders: 'mainFolders',
    }),
    beingUploadedVideos() {
      const completedVideos = this.files.filter((file) => {
        if (file.progress.uploadComplete) {
          return file
        }
      })
      return this.files.length - completedVideos.length
    },
    allVideosCompleted() {
      return this.files.every((file) => {
        return file.progress.uploadComplete
      })
    },
    atleastOneVideoCompleted() {
      return this.files.some((file) => {
        return file.progress.uploadComplete === true
      })
    },
  },
  watch: {
    files() {
      // const stillUploadingVideos = files.map((file) => {});
    },
  },
  mounted() {
    this.$root.$on('cancelUploadList', () => {
      this.handleCancel()
    })
    this.$root.$on('forcePauseUploadList', () => {
      this.pauseUpload()
    })
  },
  beforeDestroy() {
    this.$root.$off('cancelUploadList')
    this.$root.$off('forcePauseUploadList')
  },
  methods: {
    ...mapMutations('user', ['UPDATE_IMPORT_VIDEOS_LEFT']),
    checkIfAlreadyInPage(folderId) {
      if (folderId) {
        if (this.$route.params.id === folderId) return true
        return false
      } else {
        if (this.$route.name === 'Videos') return true
        return false
      }
    },
    pauseUpload() {
      this.handlePause()
      for (let file of this.files) {
        this.pausedBytes.push(
          this.calcBytes(file.progress.bytesUploaded, file.progress.bytesTotal)
        )
      }
    },
    findFolderById(folder_id) {
      return this.folders.find(({ id }) => id === folder_id)
    },
    getFolderPath(id, fileName) {
      let theFolder = this.findFolderById(id)
      let folderArray = [theFolder]
      let searchMoreFolders = true
      if (theFolder) {
        do {
          let folder = folderArray[folderArray.length - 1]
          if (folder.parent_folder_id && folder.id) {
            let parentFolderId = folder.parent_folder_id
            let parentFolder = this.findFolderById(parentFolderId)
            folderArray.push(parentFolder)
          } else {
            searchMoreFolders = false
          }
        } while (searchMoreFolders)
        folderArray = folderArray.map((folder) => folder.name)
        folderArray.push('')
        folderArray = folderArray.reverse()
        let folderPathString = folderArray.join('/')
        return fileName ? `${folderPathString}/${fileName}` : folderPathString
      } else {
        return fileName ? '/' + fileName : ''
      }
    },
    goToFolder(folderId) {
      if (folderId) {
        if (this.$route.params.id === folderId) return
        this.$router.push({ name: 'Folders', params: { id: folderId } })
      } else {
        if (this.$route.name != 'Videos') this.$router.push({ name: 'Videos' })
      }
    },
    goToVideo(videoId) {
      this.$router.push({ name: 'Videos' })
      setTimeout(
        function () {
          this.$router.push({ name: 'VideoPage', params: { id: videoId } })
        }.bind(this),
        50
      )
    },
    calcBytes(atual, total) {
      return `${prettyBytes(atual)} de ${prettyBytes(total)}`
    },
    calcByte(number) {
      return `${prettyBytes(number)}`
    },
    calcFakePercent(progressObject) {
      if (progressObject.percentage === 100 && !progressObject.uploadComplete)
        return 99
      return progressObject.percentage
    },
    calcPercent(atual, total) {
      return parseInt((atual / total) * 100, 10)
    },
    onScroll() {
      this.scrollInvoked++
    },
    resumePause() {
      if (this.paused) {
        this.handleResume()
        this.pausedBytes = []
      } else {
        this.handlePause()
        for (let file of this.files) {
          this.pausedBytes.push(
            this.calcBytes(
              file.progress.bytesUploaded,
              file.progress.bytesTotal
            )
          )
        }
      }
    },
    handleMaximize() {
      this.opened = true
    },
    handleMinimize() {
      this.opened = false
    },
    handleConfirmCancel() {
      if (this.percent >= 100) {
        this.handleCancel()
      } else {
        this.$root.$emit('confirmCancelUploadModal')
      }
    },
    handleRemoveFromQueue(file) {
      this.$root.$emit('removeFromQueue', file)
    },
    handleCleanConcluded() {
      this.files.map((file) => {
        file.progress.uploadComplete && this.handleRemoveFromQueue(file)
      })
    },
  },
}
</script>

<style>
.float-card {
  position: fixed !important;
  bottom: 20px;
  right: 20px;
  z-index: 10;
  width: 400px;
}

ul.uploads-list {
  list-style: none;
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 10px;
  font-size: 0.9rem;
}
ul.uploads-list li.list-item {
  margin: 10px 0;
  display: flex;
  border: 1px solid blue;
}
ul.uploads-list li p {
  margin: 0;
}
ul.uploads-list li p:last-of-type {
  font-size: 0.7rem;
  opacity: 0.7;
}
div.action-buttons {
  margin: 0px;
  border: 1px solid red;
}
.noPointer {
  cursor: default !important;
}
</style>
